import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  Box,
  Camera,
  Captions,
  Check,
  Eye,
  EyeOff,
  GitMerge,
  Grid3X3,
  Image,
  MapPin,
  Moon,
  RotateCcw,
  Shapes,
  Spline,
  Square,
  Sun,
  SwatchBook,
  Upload,
  Waves,
  Zap,
} from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import DraggablePanel from "./DraggablePanel";
import Checkbox from "../ui/Checkbox";
import FieldLabel from "../ui/FieldLabel";
import Tooltip from "../ui/Tooltip";
import {
  EllipsoidIcon,
  EntireObjectIcon,
  ExplosionIcon,
  JointIcon,
  VisualMeshIcon,
} from "../ui/Icons";
import {
  normalizeCollisionGeomHexColor,
  useViewerActions,
  useViewerStore,
} from "../../stores/viewerStore";
import { getAssetActions, getAssetEntries } from "../../stores/assetStore";
import { useSceneActions, useSceneStore } from "../../stores/sceneStore";
import {
  BODY_VISIBILITY_UI_EVENT,
  getBodyVisibilityUiState,
  setBodyVisibilityUiState,
} from "../../selected-body-visibility";
import {
  JOINT_VISIBILITY_UI_EVENT,
  getJointVisibilityUiState,
  setJointVisibilityUiState,
} from "../../selected-joint-visibility";
import {
  SITE_VISIBILITY_UI_EVENT,
  getSiteVisibilityUiState,
  setSiteVisibilityUiState,
} from "../../selected-site-visibility";
import {
  CONSTRAINT_VISIBILITY_UI_EVENT,
  getConstraintVisibilityUiState,
  setConstraintVisibilityUiState,
} from "../../selected-constraint-visibility/visibilityEvents";
import {
  CONSTRAINT_MARKER_LINE_WIDTH_MAX_PERCENT,
  CONSTRAINT_MARKER_LINE_WIDTH_MIN_PERCENT,
  CONSTRAINT_MARKER_SCALE_MAX_PERCENT,
  CONSTRAINT_MARKER_SCALE_MIN_PERCENT,
  CONSTRAINT_MARKER_SETTINGS_EVENT,
  CONSTRAINT_MARKER_SETTINGS_STEP_PERCENT,
  dispatchConstraintMarkerSettings,
  readConstraintMarkerSettings,
} from "../../selected-constraint-visibility/markerSettingsEvents";
import {
  SCENE_INVENTORY_LABEL_VISIBILITY_EVENT,
  getSceneInventoryLabelVisibilityState,
  setSceneInventoryLabelVisibilityState,
} from "../../scene-inventory-labels";
import {
  COLLISION_GEOM_KIND_KEYS,
  COLLISION_GEOM_KIND_LABELS,
  DEFAULT_COLLISION_GEOM_COLORS_BY_KIND,
} from "../../utils/collisionGeomKinds";
import {
  JOINT_MARKER_SCALE_DEFAULT_PERCENT,
  JOINT_MARKER_SCALE_EVENT,
  JOINT_MARKER_SCALE_MAX_PERCENT,
  JOINT_MARKER_SCALE_MIN_PERCENT,
  JOINT_MARKER_SCALE_STEP_PERCENT,
  clampJointMarkerScalePercent,
  dispatchJointMarkerScalePercent,
  readJointMarkerScalePercent,
} from "../../selected-joint-visibility/jointMarkerScaleEvents";
import { SCENE_GRAPH_NODE_COLORS } from "../../utils/sceneGraphNodeAppearance";
import {
  FLOOR_APPEARANCE_MODES,
  FLOOR_FINISHES,
  getFloorAppearanceOptions,
  getFloorAppearancePalette,
  getFloorAppearancePresetColorHex,
  setFloorAppearanceOptions,
  subscribeFloorAppearanceOptions,
} from "../../utils/floorAppearanceOptions";
import { useWindowEventState } from "../../hooks/useWindowEventState";
import {
  readReducedMotionPreference,
  setReducedMotionPreference,
  subscribeReducedMotionPreference,
} from "../../utils/reducedMotionPreference";
import {
  cacheBlobByHash,
  createFallbackBlobUrl,
  hashFile,
} from "../../utils/scene-loader/blobCache";
import { saveManifest } from "../../utils/idb";
import { logger } from "../../utils/logger";
import { formatBytes } from "../../utils/importLimits";
import MenuSelect from "../ui/MenuSelect";

const VIEWPORT_BODY_VISIBILITY_EVENT = "viewport-body-visibility";
const VIEWPORT_CAMERA_VISIBILITY_EVENT = "viewport-camera-visibility";

const sectionClass = "rounded-lg border border-gray-800/80 bg-gray-950/30";
const rowClass = "flex min-w-0 items-center justify-between gap-3 px-3 py-2";
const iconButtonClass =
  "flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-700/70 hover:text-gray-100 disabled:cursor-not-allowed disabled:text-gray-600 disabled:hover:bg-transparent";
const MAX_BACKGROUND_UPLOAD_BYTES = 50 * 1024 * 1024;
const SHOW_BACKGROUND_UPLOAD = import.meta.env.DEV;

function ToggleButton({ on, onClick, label, disabled = false, IconOn = Check, IconOff = null }) {
  const Icon = on ? IconOn : IconOff;
  return (
    <Tooltip content={label}>
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        aria-label={label}
        aria-pressed={on}
        className={`${iconButtonClass} ${on ? "bg-gray-700/80 text-white" : ""}`}
      >
        {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden /> : null}
      </button>
    </Tooltip>
  );
}

function VisibilityRow({
  Icon,
  iconClassName = "h-4 w-4",
  graphType,
  label,
  layerVisible,
  onToggleLayer,
  labelsVisible,
  onToggleLabels,
  children,
}) {
  const nodeAppearance = graphType ? SCENE_GRAPH_NODE_COLORS[graphType] : null;
  return (
    <div className={sectionClass}>
      <div className={rowClass}>
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white"
            style={
              nodeAppearance
                ? {
                    backgroundColor: nodeAppearance.base,
                    border: `1.5px solid ${nodeAppearance.stroke}`,
                  }
                : undefined
            }
          >
            <Icon className={iconClassName} aria-hidden />
          </span>
          <span className="min-w-0 truncate text-sm font-medium text-gray-100">{label}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {typeof layerVisible === "boolean" ? (
            <ToggleButton
              on={layerVisible}
              onClick={onToggleLayer}
              label={`${layerVisible ? "Hide" : "Show"} ${label.toLowerCase()} in 3D scene`}
              IconOn={Eye}
              IconOff={EyeOff}
            />
          ) : null}
          {typeof labelsVisible === "boolean" ? (
            <ToggleButton
              on={labelsVisible}
              onClick={onToggleLabels}
              label={`${labelsVisible ? "Hide" : "Show"} ${label.toLowerCase()} labels`}
              IconOn={Captions}
              IconOff={Captions}
            />
          ) : null}
        </div>
      </div>
      {children ? <div className="border-t border-gray-800/80 px-3 py-3">{children}</div> : null}
    </div>
  );
}

function CollisionColorSettings() {
  const { collisionGeomColor, collisionGeomColorMode, collisionGeomColorsByKind } = useViewerStore(
    useShallow((s) => ({
      collisionGeomColor: s.collisionGeomColor,
      collisionGeomColorMode: s.collisionGeomColorMode,
      collisionGeomColorsByKind: s.collisionGeomColorsByKind,
    }))
  );
  const viewerActions = useViewerActions();
  const [colorTextDraft, setColorTextDraft] = useState(collisionGeomColor);
  const [kindHexDrafts, setKindHexDrafts] = useState(() =>
    Object.fromEntries(
      COLLISION_GEOM_KIND_KEYS.map((kind) => [
        kind,
        collisionGeomColorsByKind[kind] || DEFAULT_COLLISION_GEOM_COLORS_BY_KIND[kind] || "#888888",
      ])
    )
  );

  useEffect(() => setColorTextDraft(collisionGeomColor), [collisionGeomColor]);
  useEffect(() => {
    setKindHexDrafts(
      Object.fromEntries(
        COLLISION_GEOM_KIND_KEYS.map((kind) => [
          kind,
          collisionGeomColorsByKind[kind] ||
            DEFAULT_COLLISION_GEOM_COLORS_BY_KIND[kind] ||
            "#888888",
        ])
      )
    );
  }, [collisionGeomColorsByKind]);

  const setSolidText = (raw) => {
    setColorTextDraft(raw);
    const normalized = normalizeCollisionGeomHexColor(raw);
    if (normalized) viewerActions.setCollisionGeomColor(normalized);
  };

  const setKindText = (kind, raw) => {
    setKindHexDrafts((drafts) => ({ ...drafts, [kind]: raw }));
    const normalized = normalizeCollisionGeomHexColor(raw);
    if (normalized) viewerActions.setCollisionGeomColorByKind(kind, normalized);
  };

  const modeButtons = [
    { mode: "solid", Icon: Square, label: "Single color" },
    { mode: "rainbow", Icon: SwatchBook, label: "Per-geom colors" },
    { mode: "byKind", Icon: Shapes, label: "Color by geom type" },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1">
        {modeButtons.map(({ mode, Icon, label }) => {
          const on = collisionGeomColorMode === mode;
          return (
            <Tooltip key={mode} content={label}>
              <button
                type="button"
                aria-label={label}
                aria-pressed={on}
                onClick={() => viewerActions.setCollisionGeomColorMode(mode)}
                className={`${iconButtonClass} ${on ? "bg-gray-700/80 text-white" : ""}`}
              >
                <Icon className="h-4 w-4" aria-hidden />
              </button>
            </Tooltip>
          );
        })}
      </div>

      {collisionGeomColorMode === "solid" ? (
        <div>
          <FieldLabel as="label" className="mb-1 block text-xs text-gray-400">
            Collision color
          </FieldLabel>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={collisionGeomColor}
              onChange={(event) => viewerActions.setCollisionGeomColor(event.target.value)}
              className="h-8 w-12 cursor-pointer rounded border border-gray-600"
            />
            <input
              type="text"
              value={colorTextDraft}
              onChange={(event) => setSolidText(event.target.value)}
              className="min-w-0 flex-1 rounded border border-gray-600 bg-gray-800 px-2 py-1 text-sm text-gray-100"
              placeholder="#ff2222"
            />
          </div>
        </div>
      ) : null}

      {collisionGeomColorMode === "byKind" ? (
        <div className="space-y-2">
          {COLLISION_GEOM_KIND_KEYS.map((kind) => (
            <div key={kind} className="flex min-w-0 items-center gap-2">
              <FieldLabel as="label" className="w-[4.5rem] shrink-0 text-xs text-gray-400">
                {COLLISION_GEOM_KIND_LABELS[kind]}
              </FieldLabel>
              <input
                type="color"
                value={
                  collisionGeomColorsByKind[kind] ||
                  DEFAULT_COLLISION_GEOM_COLORS_BY_KIND[kind] ||
                  "#888888"
                }
                onChange={(event) =>
                  viewerActions.setCollisionGeomColorByKind(kind, event.target.value)
                }
                className="h-8 w-12 shrink-0 cursor-pointer rounded border border-gray-600"
              />
              <input
                type="text"
                value={kindHexDrafts[kind] ?? ""}
                onChange={(event) => setKindText(kind, event.target.value)}
                className="min-w-0 flex-1 rounded border border-gray-600 bg-gray-800 px-2 py-1 text-sm text-gray-100"
                placeholder="#888888"
                aria-label={`${COLLISION_GEOM_KIND_LABELS[kind]} hex color`}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function JointMarkerSizeSettings() {
  const [markerScalePercent, setMarkerScalePercent] = useState(() => readJointMarkerScalePercent());
  useEffect(() => {
    const handler = (event) => {
      setMarkerScalePercent(
        clampJointMarkerScalePercent(
          Number(event?.detail?.percent ?? JOINT_MARKER_SCALE_DEFAULT_PERCENT)
        )
      );
    };
    window.addEventListener(JOINT_MARKER_SCALE_EVENT, handler);
    return () => window.removeEventListener(JOINT_MARKER_SCALE_EVENT, handler);
  }, []);

  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs text-gray-400">
        <span>Marker size</span>
        <span>{markerScalePercent}%</span>
      </div>
      <input
        type="range"
        min={JOINT_MARKER_SCALE_MIN_PERCENT}
        max={JOINT_MARKER_SCALE_MAX_PERCENT}
        step={JOINT_MARKER_SCALE_STEP_PERCENT}
        value={markerScalePercent}
        onChange={(event) => dispatchJointMarkerScalePercent(Number(event.target.value))}
        className="h-2 w-full slider"
        aria-label="Joint marker size percentage"
      />
    </div>
  );
}

function ConstraintMarkerSettings({ showTendonAppearanceControl = false }) {
  const [settings, setSettings] = useState(() => readConstraintMarkerSettings());
  useEffect(() => {
    const handler = (event) => {
      setSettings({ ...readConstraintMarkerSettings(), ...(event?.detail || {}) });
    };
    window.addEventListener(CONSTRAINT_MARKER_SETTINGS_EVENT, handler);
    return () => window.removeEventListener(CONSTRAINT_MARKER_SETTINGS_EVENT, handler);
  }, []);

  const updateSettings = (patch) => {
    setSettings(dispatchConstraintMarkerSettings(patch));
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1 flex items-center justify-between text-xs text-gray-400">
          <span>Anchor size</span>
          <span>{settings.markerScalePercent}%</span>
        </div>
        <input
          type="range"
          min={CONSTRAINT_MARKER_SCALE_MIN_PERCENT}
          max={CONSTRAINT_MARKER_SCALE_MAX_PERCENT}
          step={CONSTRAINT_MARKER_SETTINGS_STEP_PERCENT}
          value={settings.markerScalePercent}
          onChange={(event) => updateSettings({ markerScalePercent: Number(event.target.value) })}
          className="h-2 w-full slider"
          aria-label="Constraint and tendon anchor size percentage"
        />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between text-xs text-gray-400">
          <span>Line thickness</span>
          <span>{settings.lineWidthPercent}%</span>
        </div>
        <input
          type="range"
          min={CONSTRAINT_MARKER_LINE_WIDTH_MIN_PERCENT}
          max={CONSTRAINT_MARKER_LINE_WIDTH_MAX_PERCENT}
          step={CONSTRAINT_MARKER_SETTINGS_STEP_PERCENT}
          value={settings.lineWidthPercent}
          onChange={(event) => updateSettings({ lineWidthPercent: Number(event.target.value) })}
          className="h-2 w-full slider"
          aria-label="Constraint and tendon line thickness percentage"
        />
      </div>
      <Checkbox
        label="Anchor spheres"
        checked={settings.anchorSpheresVisible}
        onChange={(event) => updateSettings({ anchorSpheresVisible: event.target.checked })}
        className="w-auto px-0 py-0 text-xs text-gray-300"
        aria-label="Show constraint and tendon anchor spheres"
      />
      {showTendonAppearanceControl ? (
        <Checkbox
          label="Use appearance parsed from file"
          checked={settings.tendonParsedAppearance}
          onChange={(event) => updateSettings({ tendonParsedAppearance: event.target.checked })}
          className="w-auto px-0 py-0 text-xs text-gray-300"
          aria-label="Use tendon appearance parsed from file"
        />
      ) : null}
    </div>
  );
}

function SceneAppearancePresetSettings() {
  const [floorAppearance, setFloorAppearance] = useState(() => getFloorAppearanceOptions());

  useEffect(() => {
    return subscribeFloorAppearanceOptions(() => {
      setFloorAppearance({ ...getFloorAppearanceOptions() });
    });
  }, []);

  const modeButtons = [
    { mode: FLOOR_APPEARANCE_MODES.light, Icon: Sun, label: "Light mode" },
    { mode: FLOOR_APPEARANCE_MODES.dark, Icon: Moon, label: "Dark mode" },
  ];

  const applyPreset = (mode) => {
    setFloorAppearance(
      setFloorAppearanceOptions({
        mode,
        floorColorHex: null,
        floorOpacity: null,
        floorFinish: null,
      })
    );
  };

  return (
    <div
      className="inline-flex rounded-md border border-gray-700 bg-gray-900/30 p-0.5"
      role="group"
      aria-label="Scene appearance preset"
    >
      {modeButtons.map(({ mode, Icon, label }) => {
        const on = floorAppearance.mode === mode;
        return (
          <button
            key={mode}
            type="button"
            aria-label={`${label} preset`}
            aria-pressed={on}
            onClick={() => applyPreset(mode)}
            className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs transition-colors select-none ${
              on ? "bg-gray-700 text-gray-100" : "text-gray-300 hover:text-gray-100"
            }`}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            <span>{mode === FLOOR_APPEARANCE_MODES.light ? "Light Preset" : "Dark Preset"}</span>
          </button>
        );
      })}
    </div>
  );
}

function FloorAppearanceSettings() {
  const [floorAppearance, setFloorAppearance] = useState(() => getFloorAppearanceOptions());

  useEffect(() => {
    return subscribeFloorAppearanceOptions(() => {
      setFloorAppearance({ ...getFloorAppearanceOptions() });
    });
  }, []);

  const presetColorHex = getFloorAppearancePresetColorHex(floorAppearance.mode);
  const floorColorHex = floorAppearance.floorColorHex || presetColorHex;
  const floorOpacity = Math.round(
    Math.max(0, Math.min(1, Number(floorAppearance.floorOpacity ?? 1))) * 100
  );
  const floorFinish = getFloorAppearancePalette(floorAppearance).floorFinish;
  const finishOptions = [
    { value: FLOOR_FINISHES.matte, label: "Matte" },
    { value: FLOOR_FINISHES.satin, label: "Satin" },
    { value: FLOOR_FINISHES.glossy, label: "Glossy" },
  ];

  const updateFloorAppearance = (patch) => {
    setFloorAppearance(setFloorAppearanceOptions(patch));
  };

  return (
    <div className="min-w-0 flex-1 space-y-2">
      <div className="flex min-w-0 items-center gap-2">
        <FieldLabel as="label" className="shrink-0" htmlFor="visibility-floor-color">
          Color
        </FieldLabel>
        <input
          id="visibility-floor-color"
          type="color"
          value={floorColorHex}
          onChange={(event) => updateFloorAppearance({ floorColorHex: event.target.value })}
          className="h-7 w-10 shrink-0 cursor-pointer rounded border border-gray-600 bg-gray-800 p-0.5"
          aria-label="Floor color"
        />
        <span className="min-w-0 flex-1 truncate text-xs tabular-nums text-gray-400">
          {floorColorHex}
        </span>
        <Tooltip content="Reset floor to preset">
          <button
            type="button"
            onClick={() =>
              updateFloorAppearance({
                floorColorHex: null,
                floorOpacity: null,
                floorFinish: null,
              })
            }
            className={iconButtonClass}
            aria-label="Reset floor to preset"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
          </button>
        </Tooltip>
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between text-xs text-gray-400">
          <span>Transparency</span>
          <span>{100 - floorOpacity}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={100 - floorOpacity}
          onChange={(event) => {
            const transparencyPercent = Math.max(0, Math.min(100, Number(event.target.value)));
            updateFloorAppearance({ floorOpacity: (100 - transparencyPercent) / 100 });
          }}
          className="h-2 w-full slider"
          aria-label="Floor transparency percentage"
        />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between text-xs text-gray-400">
          <span>Finish</span>
        </div>
        <div className="grid grid-cols-3 rounded-md border border-gray-700 bg-gray-900/30 p-0.5">
          {finishOptions.map((option) => {
            const on = floorFinish === option.value;
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={on}
                onClick={() => updateFloorAppearance({ floorFinish: option.value })}
                className={`min-w-0 rounded px-2 py-1 text-xs transition-colors ${
                  on ? "bg-gray-700 text-gray-100" : "text-gray-300 hover:text-gray-100"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BackgroundSettings() {
  const inputRef = useRef(null);
  const background = useSceneStore((state) => state.background);
  const backgroundLibrary = useSceneStore((state) => state.backgroundLibrary);
  const sceneActions = useSceneActions();
  const [uploadError, setUploadError] = useState("");

  const projectBackgrounds = useMemo(
    () =>
      Object.values(backgroundLibrary || {}).sort((a, b) =>
        String(a.name || a.fileName || a.assetId).localeCompare(
          String(b.name || b.fileName || b.assetId)
        )
      ),
    [backgroundLibrary]
  );

  const selectedValue =
    background?.type === "color"
      ? "color"
      : background?.type === "asset" && background.assetId
        ? background.assetId
        : "builtin";
  const backgroundColorHex =
    background?.type === "color" && /^#?[0-9a-f]{6}$/i.test(background.colorHex || "")
      ? background.colorHex.startsWith("#")
        ? background.colorHex
        : `#${background.colorHex}`
      : "#111827";

  const selectBackground = (value) => {
    if (!value || value === "builtin") {
      sceneActions.setBackground({ type: "builtin" });
      return;
    }
    if (value === "color") {
      sceneActions.setBackground({ type: "color", colorHex: backgroundColorHex });
      return;
    }
    const saved = backgroundLibrary?.[value];
    sceneActions.setBackground({
      type: "asset",
      assetId: value,
      name: saved?.name || saved?.fileName || value,
      fileName: saved?.fileName || saved?.name || value,
      mimeType: saved?.mimeType || "",
    });
  };

  const handleUpload = async (event) => {
    const input = event.target;
    const file = input?.files?.[0];
    if (!file) return;

    const isSupported =
      /\.(exr|hdr|jpe?g|png)$/i.test(file.name || "") ||
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/vnd.radiance" ||
      file.type === "image/x-exr" ||
      file.type === "image/exr";
    if (!isSupported) {
      setUploadError("Use EXR, HDR, JPG, or PNG.");
      input.value = "";
      return;
    }
    if (file.size > MAX_BACKGROUND_UPLOAD_BYTES) {
      setUploadError(
        `Use a 4K or smaller background file under ${formatBytes(MAX_BACKGROUND_UPLOAD_BYTES)}.`
      );
      input.value = "";
      return;
    }

    try {
      const hash = await hashFile(file);
      const assetId = `bg:sha256:${hash}`;
      const baseName = (file.name || "").replace(/\.[^/.]+$/, "").trim() || "Scene Background";
      const sources = { idbKey: hash };
      try {
        await cacheBlobByHash(hash, file);
      } catch (error) {
        logger.warn("[VisibilitySettingsPanel] Failed to persist background asset", error);
        sources.url = createFallbackBlobUrl(file);
        delete sources.idbKey;
      }

      getAssetActions().registerAssets([
        {
          id: assetId,
          kind: "texture",
          name: file.name || baseName,
          status: "idle",
          bytes: file.size,
          hash,
          sources,
        },
      ]);
      try {
        await saveManifest(Object.values(getAssetEntries() || {}));
      } catch (error) {
        logger.warn("[VisibilitySettingsPanel] Failed to save asset manifest", error);
      }
      sceneActions.setBackground({
        type: "asset",
        assetId,
        name: baseName,
        fileName: file.name || baseName,
        mimeType:
          file.type ||
          (/\.exr$/i.test(file.name || "")
            ? "image/x-exr"
            : /\.hdr$/i.test(file.name || "")
              ? "image/vnd.radiance"
              : ""),
      });
      setUploadError("");
    } catch (error) {
      logger.warn("[VisibilitySettingsPanel] Failed to upload scene background", error);
      setUploadError("Could not load background.");
    } finally {
      input.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex min-w-0 items-center gap-2">
        <MenuSelect
          value={selectedValue}
          onChange={selectBackground}
          className="min-w-0 flex-1"
          triggerClassName="min-w-0"
          options={[
            { value: "builtin", label: "Built-in studio" },
            { value: "color", label: "Plain color" },
            ...projectBackgrounds.map((entry) => ({
              value: entry.assetId,
              label: entry.name || entry.fileName || entry.assetId,
            })),
          ]}
          aria-label="Scene background"
        />
        {SHOW_BACKGROUND_UPLOAD ? (
          <Tooltip content="Upload background">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className={iconButtonClass}
              aria-label="Upload background"
            >
              <Upload className="h-3.5 w-3.5" aria-hidden />
            </button>
          </Tooltip>
        ) : null}
        <Tooltip content="Use built-in background">
          <button
            type="button"
            onClick={() => sceneActions.setBackground({ type: "builtin" })}
            className={iconButtonClass}
            aria-label="Use built-in background"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
          </button>
        </Tooltip>
      </div>
      {selectedValue === "color" ? (
        <div className="flex min-w-0 items-center gap-2">
          <FieldLabel as="label" className="shrink-0" htmlFor="visibility-background-color">
            Color
          </FieldLabel>
          <input
            id="visibility-background-color"
            type="color"
            value={backgroundColorHex}
            onChange={(event) =>
              sceneActions.setBackground({ type: "color", colorHex: event.target.value })
            }
            className="h-7 w-10 shrink-0 cursor-pointer rounded border border-gray-600 bg-gray-800 p-0.5"
            aria-label="Plain background color"
          />
          <span className="min-w-0 truncate text-xs tabular-nums text-gray-400">
            {backgroundColorHex}
          </span>
        </div>
      ) : null}
      {SHOW_BACKGROUND_UPLOAD ? (
        <input
          ref={inputRef}
          type="file"
          accept=".exr,.hdr,.jpg,.jpeg,.png,image/x-exr,image/exr,image/jpeg,image/png,image/vnd.radiance"
          onChange={handleUpload}
          className="hidden"
        />
      ) : null}
      {uploadError ? <div className="text-xs text-red-400">{uploadError}</div> : null}
    </div>
  );
}

function ReducedMotionSettings() {
  const [enabled, setEnabled] = useState(readReducedMotionPreference);

  useEffect(() => subscribeReducedMotionPreference(setEnabled), []);

  return (
    <div className={sectionClass}>
      <div className={rowClass}>
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gray-700 bg-gray-900 text-gray-200">
            <Waves className="h-4 w-4" aria-hidden />
          </span>
          <span className="min-w-0 truncate text-sm font-medium text-gray-100">Reduced Motion</span>
        </div>
        <Checkbox
          checked={enabled}
          onChange={(event) => setEnabled(setReducedMotionPreference(event.target.checked))}
          aria-label="Reduce interface motion"
          className="w-auto px-0 py-0"
        />
      </div>
    </div>
  );
}

const VisibilitySettingsPanel = ({ open, onClose, titleIcon }) => {
  const viewer = useViewerStore(
    useShallow((s) => ({
      showVisual: s.showVisual,
      showJoints: s.showJoints,
      showCollision: s.showCollision,
      showSites: s.showSites,
      showInertials: s.showInertials,
      showConstraintMarkers: s.showConstraintMarkers,
      showActuators: s.showActuators,
    }))
  );
  const viewerActions = useViewerActions();
  const [bodyVisibilityUi, setBodyVisibilityUi] = useWindowEventState(
    BODY_VISIBILITY_UI_EVENT,
    getBodyVisibilityUiState
  );
  const [jointVisibilityUi, setJointVisibilityUi] = useWindowEventState(
    JOINT_VISIBILITY_UI_EVENT,
    getJointVisibilityUiState
  );
  const [siteVisibilityUi, setSiteVisibilityUi] = useWindowEventState(
    SITE_VISIBILITY_UI_EVENT,
    getSiteVisibilityUiState
  );
  const [constraintVisibilityUi, setConstraintVisibilityUi] = useWindowEventState(
    CONSTRAINT_VISIBILITY_UI_EVENT,
    getConstraintVisibilityUiState
  );
  const [inventoryLabels, setInventoryLabels] = useWindowEventState(
    SCENE_INVENTORY_LABEL_VISIBILITY_EVENT,
    getSceneInventoryLabelVisibilityState
  );
  const [showViewportBodies, setShowViewportBodies] = useState(true);
  const [showCameras, setShowCameras] = useState(false);
  const constraintLayerVisible =
    viewer.showConstraintMarkers && constraintVisibilityUi.constraintsVisible;
  const tendonLayerVisible = viewer.showConstraintMarkers && constraintVisibilityUi.tendonsVisible;
  const setConstraintLayerVisible = (visible) => {
    setConstraintVisibilityUi(
      setConstraintVisibilityUiState({
        constraintsVisible: !!visible,
        ...(!viewer.showConstraintMarkers && visible ? { tendonsVisible: false } : {}),
      })
    );
    if (visible && !viewer.showConstraintMarkers) {
      viewerActions.toggleShowConstraintMarkers();
    }
    if (
      !visible &&
      !getConstraintVisibilityUiState().tendonsVisible &&
      viewer.showConstraintMarkers
    ) {
      viewerActions.toggleShowConstraintMarkers();
    }
  };
  const setTendonLayerVisible = (visible) => {
    setConstraintVisibilityUi(
      setConstraintVisibilityUiState({
        tendonsVisible: !!visible,
        ...(!viewer.showConstraintMarkers && visible ? { constraintsVisible: false } : {}),
      })
    );
    if (visible && !viewer.showConstraintMarkers) {
      viewerActions.toggleShowConstraintMarkers();
    }
    if (
      !visible &&
      !getConstraintVisibilityUiState().constraintsVisible &&
      viewer.showConstraintMarkers
    ) {
      viewerActions.toggleShowConstraintMarkers();
    }
  };

  useEffect(() => {
    const onBodies = (event) => setShowViewportBodies((event?.detail?.visible ?? true) === true);
    const onCameras = (event) => setShowCameras((event?.detail?.visible ?? false) === true);
    window.addEventListener(VIEWPORT_BODY_VISIBILITY_EVENT, onBodies);
    window.addEventListener(VIEWPORT_CAMERA_VISIBILITY_EVENT, onCameras);
    return () => {
      window.removeEventListener(VIEWPORT_BODY_VISIBILITY_EVENT, onBodies);
      window.removeEventListener(VIEWPORT_CAMERA_VISIBILITY_EVENT, onCameras);
    };
  }, []);

  const toggleViewportBodies = () => {
    const visible = !showViewportBodies;
    setShowViewportBodies(visible);
    window.dispatchEvent(new CustomEvent(VIEWPORT_BODY_VISIBILITY_EVENT, { detail: { visible } }));
  };
  const toggleCameras = () => {
    const visible = !showCameras;
    setShowCameras(visible);
    window.dispatchEvent(
      new CustomEvent(VIEWPORT_CAMERA_VISIBILITY_EVENT, { detail: { visible } })
    );
  };

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Visibility Settings"
      titleIcon={titleIcon}
      initialSize={{ width: 390, height: 560 }}
      minWidth={340}
      minHeight={360}
    >
      <div className="flex-1 overflow-auto p-3 text-gray-200">
        <div className="space-y-2">
          <ReducedMotionSettings />
          <VisibilityRow Icon={Sun} label="Scene Appearance">
            <SceneAppearancePresetSettings />
          </VisibilityRow>
          <VisibilityRow Icon={Square} label="Floor">
            <FloorAppearanceSettings />
          </VisibilityRow>
          <VisibilityRow Icon={Image} label="Background">
            <BackgroundSettings />
          </VisibilityRow>
          <VisibilityRow
            Icon={EntireObjectIcon}
            iconClassName="h-3.5 w-3.5"
            graphType="body"
            label="Object"
            layerVisible={showViewportBodies}
            onToggleLayer={toggleViewportBodies}
            labelsVisible={bodyVisibilityUi.objectLabels}
            onToggleLabels={() =>
              setBodyVisibilityUi(
                setBodyVisibilityUiState({ objectLabels: !getBodyVisibilityUiState().objectLabels })
              )
            }
          />
          <VisibilityRow
            Icon={Box}
            graphType="body"
            label="Body"
            layerVisible={showViewportBodies}
            onToggleLayer={toggleViewportBodies}
            labelsVisible={bodyVisibilityUi.labels}
            onToggleLabels={() =>
              setBodyVisibilityUi(
                setBodyVisibilityUiState({ labels: !getBodyVisibilityUiState().labels })
              )
            }
          />
          <VisibilityRow
            Icon={VisualMeshIcon}
            iconClassName="h-3.5 w-3.5"
            graphType="mesh"
            label="Visual Geometry"
            layerVisible={viewer.showVisual}
            onToggleLayer={viewerActions.toggleShowVisual}
            labelsVisible={inventoryLabels.visual}
            onToggleLabels={() =>
              setInventoryLabels(
                setSceneInventoryLabelVisibilityState({
                  visual: !getSceneInventoryLabelVisibilityState().visual,
                })
              )
            }
          />
          <VisibilityRow
            Icon={Grid3X3}
            graphType="mesh"
            label="Mesh"
            layerVisible={viewer.showVisual}
            onToggleLayer={viewerActions.toggleShowVisual}
            labelsVisible={inventoryLabels.mesh}
            onToggleLabels={() =>
              setInventoryLabels(
                setSceneInventoryLabelVisibilityState({
                  mesh: !getSceneInventoryLabelVisibilityState().mesh,
                })
              )
            }
          />
          <VisibilityRow
            Icon={JointIcon}
            iconClassName="h-3.5 w-3.5"
            graphType="joint"
            label="Joint"
            layerVisible={viewer.showJoints}
            onToggleLayer={viewerActions.toggleShowJoints}
            labelsVisible={!jointVisibilityUi.labelsOff}
            onToggleLabels={() =>
              setJointVisibilityUi(
                setJointVisibilityUiState({ labelsOff: !getJointVisibilityUiState().labelsOff })
              )
            }
          >
            <JointMarkerSizeSettings />
          </VisibilityRow>
          <VisibilityRow
            Icon={ExplosionIcon}
            iconClassName="h-3.5 w-3.5"
            graphType="geom"
            label="Collision"
            layerVisible={viewer.showCollision}
            onToggleLayer={viewerActions.toggleShowCollision}
            labelsVisible={inventoryLabels.collision}
            onToggleLabels={() =>
              setInventoryLabels(
                setSceneInventoryLabelVisibilityState({
                  collision: !getSceneInventoryLabelVisibilityState().collision,
                })
              )
            }
          >
            <CollisionColorSettings />
          </VisibilityRow>
          <VisibilityRow
            Icon={MapPin}
            graphType="site"
            label="Site"
            layerVisible={viewer.showSites}
            onToggleLayer={viewerActions.toggleShowSites}
            labelsVisible={!siteVisibilityUi.labelsOff}
            onToggleLabels={() =>
              setSiteVisibilityUi(
                setSiteVisibilityUiState({ labelsOff: !getSiteVisibilityUiState().labelsOff })
              )
            }
          />
          <VisibilityRow
            Icon={EllipsoidIcon}
            iconClassName="h-3.5 w-3.5"
            graphType="inertial"
            label="Inertial"
            layerVisible={viewer.showInertials}
            onToggleLayer={viewerActions.toggleShowInertials}
            labelsVisible={inventoryLabels.inertial}
            onToggleLabels={() =>
              setInventoryLabels(
                setSceneInventoryLabelVisibilityState({
                  inertial: !getSceneInventoryLabelVisibilityState().inertial,
                })
              )
            }
          />
          <VisibilityRow
            Icon={GitMerge}
            graphType="equality"
            label="Constraint"
            layerVisible={constraintLayerVisible}
            onToggleLayer={() => setConstraintLayerVisible(!constraintLayerVisible)}
            labelsVisible={!constraintVisibilityUi.labelsOff}
            onToggleLabels={() =>
              setConstraintVisibilityUi(
                setConstraintVisibilityUiState({
                  labelsOff: !getConstraintVisibilityUiState().labelsOff,
                })
              )
            }
          >
            <ConstraintMarkerSettings />
          </VisibilityRow>
          <VisibilityRow
            Icon={Spline}
            graphType="tendon"
            label="Tendon"
            layerVisible={tendonLayerVisible}
            onToggleLayer={() => setTendonLayerVisible(!tendonLayerVisible)}
            labelsVisible={!constraintVisibilityUi.tendonLabelsOff}
            onToggleLabels={() =>
              setConstraintVisibilityUi(
                setConstraintVisibilityUiState({
                  tendonLabelsOff: !getConstraintVisibilityUiState().tendonLabelsOff,
                })
              )
            }
          >
            <ConstraintMarkerSettings showTendonAppearanceControl />
          </VisibilityRow>
          <VisibilityRow
            Icon={Zap}
            graphType="actuator"
            label="Actuator"
            layerVisible={viewer.showActuators}
            onToggleLayer={viewerActions.toggleShowActuators}
            labelsVisible={inventoryLabels.actuator}
            onToggleLabels={() =>
              setInventoryLabels(
                setSceneInventoryLabelVisibilityState({
                  actuator: !getSceneInventoryLabelVisibilityState().actuator,
                })
              )
            }
          />
          <VisibilityRow
            Icon={Camera}
            graphType="camera"
            label="Camera"
            layerVisible={showCameras}
            onToggleLayer={toggleCameras}
            labelsVisible={inventoryLabels.camera}
            onToggleLabels={() =>
              setInventoryLabels(
                setSceneInventoryLabelVisibilityState({
                  camera: !getSceneInventoryLabelVisibilityState().camera,
                })
              )
            }
          />
        </div>
      </div>
    </DraggablePanel>
  );
};

export default React.memo(VisibilitySettingsPanel);
